import { groupBy, keyBy, sortBy, uniqBy } from "lodash-es";
import { createSelector } from "reselect";

import { OrganizationSetting } from "@mapmycustomers/shared";
import { Activity } from "@mapmycustomers/shared/types/entity";
import User from "@mapmycustomers/shared/types/User";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import formatISODate from "@mapmycustomers/shared/util/formatters/formatISODate";

import {
  getMe,
  getOrganizationSettings,
  getOrganizationSettingValue,
  isFieldViewEnabled,
} from "@app/store/iam";
import { getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import { parseApiDate, parseApiDateWithTz } from "@app/util/parsers";
import getCheckInEntityRef from "@app/util/timeline/getCheckInEntityRef";
import normalizeRawTimelineEvents from "@app/util/timeline/normalizeRawTimelineEvents";

import {
  FIELD_VIEW_TIMELINE_ACTIONS,
  HEATMAP_CHECKIN_THRESHOLD,
  MAX_SELECTED_USERS,
} from "./consts";
import UserLocationDatePeriod from "./enums/UserLocationDatePeriod";
import UserCheckInLocation from "./types/UserCheckInLocation";
import UserLocation from "./types/UserLocation";
import UserLocationCheckInGroup from "./types/UserLocationCheckInGroup";
import UserLocationDayGroup from "./types/UserLocationDayGroup";
import UserLocationTimelineEvent from "./types/UserLocationTimelineEvent";
import WidgetsData from "./types/WidgetsData";
import convertTimelineEventToLocation from "./utils/convertTimelineEventToLocation";
import getDateRangeForSelectedDatePeriod from "./utils/getDateRangeForSelectedDatePeriod";
import isUserLocationLayerAvailable from "./utils/isUserLocationLayerAvailable";

const userLocationsViewState = (state: RootState) => state.mapLayers.userLocations.view;

// Raw audit rows as fetched (newest-first, geo-filtered). Source of truth for both views below.
export const getEvents = createSelector(userLocationsViewState, ({ events }) => events);

// Exported (not private) — the timeline container (Task 7) imports it too.
export const getOrgTimeZone = (state: RootState): string | undefined =>
  getOrganizationSettingValue(state, OrganizationSetting.TIMEZONE);

// The visited account/contact map pins (fetched per check-in by onFetchCheckInPins, keyed by id).
// Declared before getLocations because that selector resolves each check-in's entity through them.
export const getAccountPins = createSelector(
  userLocationsViewState,
  ({ accountPins }) => accountPins
);

export const getContactPins = createSelector(
  userLocationsViewState,
  ({ contactPins }) => contactPins
);

// Map/check-in view: derive the UserLocation[] the rest of the layer consumes. A check-in resolves its
// visited record's pin here (via accountPins/contactPins), so it surfaces only once that pin has been
// fetched; until then the event is a plain trail point.
export const getLocations = createSelector(
  [getEvents, getOrgTimeZone, getAccountPins, getContactPins],
  (events, orgTimeZone, accountPins, contactPins): UserLocation[] =>
    events.map((event) =>
      convertTimelineEventToLocation(event, orgTimeZone, accountPins, contactPins)
    )
);

export const isUserLocationLoading = createSelector(
  userLocationsViewState,
  ({ loading }) => loading
);

export const isPinsLoading = createSelector(
  userLocationsViewState,
  ({ pinsLoading }) => pinsLoading
);

export const getPendingLegendKeys = createSelector(
  userLocationsViewState,
  ({ pendingColorKey, pendingShapeKey }) => ({ pendingColorKey, pendingShapeKey })
);

export const getDisabledUserIds = createSelector(
  userLocationsViewState,
  ({ disabledUserIds }) => disabledUserIds
);

export const getSelectedUserIds = createSelector(
  userLocationsViewState,
  ({ selectedUserIds }) => selectedUserIds
);

// Timeline view: normalize raw audit rows into BaseTimelineEvent rows. The timeline component casts
// these to the TimelineEvent union for EventItem, which narrows each row on `action`.
//
// Scoped to the selected reps. Hiding a rep in the legend drops them from selectedUserIds but does NOT
// refetch, so their events stay in state — without this filter their cards would linger in the list,
// look selectable, and resolve to no location at all.
//
// Defined below getSelectedUserIds on purpose: these are module-level consts, so referencing it any
// earlier would read it before initialisation.
export const getTimelineEvents = createSelector(
  getEvents,
  getSelectedUserIds,
  (events, selectedUserIds) => {
    const selectedUserIdSet = new Set(selectedUserIds);
    return normalizeRawTimelineEvents(
      events.filter((event) => selectedUserIdSet.has(event.updatedBy.id)),
      FIELD_VIEW_TIMELINE_ACTIONS
    );
  }
);

export const getSelectedUsers = createSelector(
  getSelectedUserIds,
  getUsers,
  (selectedUserIds, users) => {
    const usersById = keyBy(users, "id");
    return selectedUserIds.map((id) => usersById[id]).filter(isDefined);
  }
);

export const isSelectedUsersCapped = createSelector(
  getSelectedUserIds,
  (selectedUserIds) => selectedUserIds.length >= MAX_SELECTED_USERS
);

export const getShowOtherPins = createSelector(
  userLocationsViewState,
  ({ showOtherPins }) => showOtherPins
);

// The general map is hidden only when we're actually in Field View (reps selected) AND the
// user turned the toggle off. Guarding on selectedUserIds keeps it correct even though
// selectedUserIds can be emptied via paths other than the Exit button (e.g. setDisabledUserIds).
export const isGeneralMapHiddenInFieldView = createSelector(
  getSelectedUserIds,
  getShowOtherPins,
  (selectedUserIds, showOtherPins) => selectedUserIds.length > 0 && !showOtherPins
);

export const getHighlightedLocationId = createSelector(
  userLocationsViewState,
  ({ highlightedLocationId }) => highlightedLocationId
);

export const getHoveredUserLocationDayGroup = createSelector(
  userLocationsViewState,
  ({ hoveredUserLocationDayGroup }) => hoveredUserLocationDayGroup
);

export const getHoveredTrailPoint = createSelector(
  userLocationsViewState,
  ({ hoveredTrailPoint }) => hoveredTrailPoint
);

export const getSelectedLocationId = createSelector(
  userLocationsViewState,
  ({ selectedLocationId }) => selectedLocationId
);

// Split the selected users' events into one group per (user, day). Events are stored newest-first, so
// reversing each group yields ascending-by-createdAt order — the single canonical order the polyline +
// playback rely on. Reversed ONCE here; no consumer re-sorts.
// The day key is the org-tz calendar day precomputed on each event (UserLocation.createdAtIsoDate), so
// a given event lands in the same day group for every viewer regardless of device timezone.
export const getVisibleUserLocationDayGroups = createSelector(
  getSelectedUsers,
  getLocations,
  (selectedUsers, events): UserLocationDayGroup[] => {
    const eventsByUserId = groupBy(events, (event) => event.userId);
    return selectedUsers.flatMap((user) =>
      Object.entries(groupBy(eventsByUserId[user.id] ?? [], (event) => event.createdAtIsoDate)).map(
        ([day, locations]) => ({ day, locations: [...locations].reverse(), user })
      )
    );
  }
);

// One avatar per (user, day) group, on the group's LAST event — where the rep ended that day, which
// is also where the playback playhead finishes, so a finished run hands off to this marker without a
// jump. Deliberately NOT filtered to non-check-ins: if the day ends with a check-in the avatar sits
// on that check-in's pin (accepted overlap — the avatar renders above it). Each group's locations are
// sorted ascending (see getVisibleUserLocationDayGroups) and never empty, so last = latest.
export const getTrailEndLocations = createSelector(
  getVisibleUserLocationDayGroups,
  (dayGroups): UserLocation[] =>
    dayGroups.map((group) => group.locations[group.locations.length - 1])
);

export const getTrailEndLocationIds = createSelector(
  getTrailEndLocations,
  (trailEndLocations): Set<UserLocation["id"]> =>
    new Set(trailEndLocations.map((location) => location.id))
);

// Every point of every visible (selected, not hidden/disabled) rep trail — used to frame
// the map camera. Pairs with getVisibleUserLocationDayGroups (grouped) and
// getTrailEndLocations (one marker per group).
export const getVisibleUserLocations = createSelector(
  getVisibleUserLocationDayGroups,
  (dayGroups): UserLocation[] => dayGroups.flatMap((group) => group.locations)
);

// The selected timeline event as a map location — the selection ring's only item, and the point the
// playhead parks on. Resolved against the VISIBLE locations: an event belonging to a hidden rep is not
// on the rail and has nothing to ring, so it must resolve to nothing rather than to a stale position.
export const getSelectedLocation = createSelector(
  getVisibleUserLocations,
  getSelectedLocationId,
  (visibleLocations, selectedLocationId): undefined | UserLocation =>
    selectedLocationId === undefined
      ? undefined
      : visibleLocations.find((location) => location.id === selectedLocationId)
);

export const getSelectedDatePeriod = createSelector(
  userLocationsViewState,
  ({ selectedDatePeriod }) => selectedDatePeriod
);

// Week/Month are the multi-day views (vs. single Day); the trail solid-hover highlight and the
// per-day date tooltips key off this.
export const getIsMultiDayPeriod = createSelector(
  getSelectedDatePeriod,
  (period) => period === UserLocationDatePeriod.Week || period === UserLocationDatePeriod.Month
);

export const getSelectedDate = createSelector(
  userLocationsViewState,
  ({ selectedDate }) => selectedDate
);

export const getSelectedDateRange = createSelector(
  getSelectedDatePeriod,
  getSelectedDate,
  (period, date) =>
    date === undefined ? undefined : getDateRangeForSelectedDatePeriod(period, date)
);

export const getWidgetsData = createSelector(
  userLocationsViewState,
  ({ widgetsData }) => widgetsData
);

export const getWidgetsDataLoading = createSelector(
  userLocationsViewState,
  ({ widgetsDataLoading }) => widgetsDataLoading
);

export const getWidgetsDataDateRange = createSelector(
  userLocationsViewState,
  ({ widgetsDataDateRange }) => widgetsDataDateRange
);

export const getEventsDateRange = createSelector(
  userLocationsViewState,
  ({ eventsDateRange }) => eventsDateRange
);

export const getSelectedDateRangeWidgetsData = createSelector(
  getWidgetsData,
  getSelectedDateRange,
  (widgetsData, range): undefined | WidgetsData => {
    if (!widgetsData || !range) {
      return undefined;
    }

    const startDate = formatISODate(range.startDate);
    const endDate = formatISODate(range.endDate);

    return widgetsData.filter((item) => item.date >= startDate && item.date <= endDate);
  }
);

// Selected reps shown as a single last-known-location marker. A rep who HAS timeline data for the period is
// drawn from it by MapPathMarkers (all their pins), so this selector skips them — no double marker. It
// returns the reps who have NO timeline data for the period but do have a last location within it: legacy
// location data that predates the timeline and was never migrated into it, surfaced so the rep is still
// placed on the map instead of vanishing. Returns [] until the events fetch settles (not loading, a fetched
// range present) so a mid-load or failed load never surfaces a stale marker. The last-location day is
// bucketed in the org timezone to match the event day groups. Feeds the marker layer and the camera fit.
export const getSelectedUsersWithOnlyLastLocationInPeriod = createSelector(
  getSelectedUsers,
  getVisibleUserLocationDayGroups,
  getSelectedDateRange,
  getEventsDateRange,
  isUserLocationLoading,
  getOrgTimeZone,
  (selectedUsers, dayGroups, selectedRange, eventsDateRange, loading, orgTimeZone): User[] => {
    if (loading || eventsDateRange === undefined || selectedRange === undefined) {
      return [];
    }
    const startIsoDate = formatISODate(selectedRange.startDate);
    const endIsoDate = formatISODate(selectedRange.endDate);
    const userIdsWithTrailData = new Set(dayGroups.map((group) => group.user.id));
    return selectedUsers.filter((user) => {
      if (!user.lastLocation || userIdsWithTrailData.has(user.id)) {
        return false;
      }
      const lastLocationIsoDate = formatISODate(
        parseApiDateWithTz(user.lastLocation.createdAt, orgTimeZone)
      );
      return lastLocationIsoDate >= startIsoDate && lastLocationIsoDate <= endIsoDate;
    });
  }
);

// Every visible (selected, not hidden/disabled) check-in, one per activity. /timeline is an audit feed,
// so a single check-in surfaces as several geo-stamped rows — the check-in event itself plus later edits
// to the same activity (note added, file added, a later completion). All carry checkInType, so all
// classify as check-ins; the audit action can't single out the check-in (it may be "activity completed"
// or an update, not "record added"), so dedupe by activity id, keeping the earliest event (the check-in,
// before any edit) so its day bucket, timestamp, and GPS stay the check-in's. Feeds CheckInMarkers, the
// CSV export, and the heatmap-threshold count, keeping all three consistent.
export const getVisibleCheckInLocations = createSelector(
  getVisibleUserLocations,
  (locations): UserCheckInLocation[] => {
    const checkInLocations = locations.filter(
      (location): location is UserCheckInLocation => location.checkIn !== undefined
    );
    return uniqBy(
      sortBy(checkInLocations, (location) => parseApiDate(location.createdAt).getTime()),
      (location) => location.checkIn.activityId
    );
  }
);

// Collapse check-ins that share the visited entity's exact coordinates into one group, mirroring the
// standard map's MultiPin (same-coordinate) grouping. A lone check-in becomes a group of length 1.
// Within a group, check-ins are ordered earliest-first — this drives the grouped-pin representative
// styling and the click -> timeline target.
export const getVisibleCheckInGroups = createSelector(
  getVisibleCheckInLocations,
  (locations): UserLocationCheckInGroup[] => {
    // A check-in whose visited entity has no resolvable geoPoint (e.g. the viewer isn't permitted to
    // see that record's location) can't be placed on the map — drop it rather than throw in-selector.
    const locatableCheckIns = locations.filter((location) => location.checkIn.entityPin.geoPoint);
    const byCoordinate = groupBy(locatableCheckIns, (location) =>
      location.checkIn.entityPin.geoPoint!.coordinates.join(",")
    );
    return Object.entries(byCoordinate).map(([id, groupLocations]) => ({
      id,
      locations: sortBy(groupLocations, (location) =>
        parseApiDate(location.checkIn.checkinAt ?? location.createdAt).getTime()
      ),
      position: groupLocations[0].checkIn.entityPin.geoPoint!.coordinates,
    }));
  }
);

// Geo events scoped to the selected reps — the shared input for the heatmap's points and the
// check-in count, so the filter runs once per events change rather than once per consumer.
// getTimelineEvents deliberately does NOT read this: it scopes on getSelectedUserIds, which keeps
// ids that getSelectedUsers drops when they are absent from the members store.
const getSelectedUsersEvents = createSelector(
  getEvents,
  getSelectedUsers,
  (events, selectedUsers): UserLocationTimelineEvent[] => {
    const selectedUserIdSet = new Set(selectedUsers.map((user) => user.id));
    return events.filter((event) => selectedUserIdSet.has(event.updatedBy.id));
  }
);

// All visible geo events bucketed by rep id — one heatmap layer (and the single-rep hotspot) is built
// per bucket. Density uses ALL geo-tagged events, not check-ins only (FR-15). The check-in count still
// drives getAutoHeatmapMode (the trigger); this selector drives what the heatmap renders.
// Derived from the events, not from getVisibleUserLocations: the heatmap reads only a position, and
// the resolved-location chain also depends on accountPins/contactPins, so a pins arrival would
// re-identity every bucket and make deck.gl re-aggregate every rep's weights texture for data the
// heatmap never draws. The point set is the same either way, because the resolved chain is itself
// scoped to the selected reps.
export const getVisibleUserLocationsByUser = createSelector(
  getSelectedUsersEvents,
  (events): Record<User["id"], UserLocationTimelineEvent[]> =>
    groupBy(events, (event) => event.updatedBy.id)
);

// The check-in count behind the threshold rule. Derived from the events, not from
// getVisibleCheckInLocations: a check-in's own timeline row already names the visited record
// (getCheckInEntityRef), so counting needs no /pins round trip — only drawing the markers does.
// /timeline emits several audit rows per check-in, so dedupe by activity id. This is an upper bound on
// what the map draws — a check-in whose record has no resolvable pin still counts here — and it is
// nonetheless the number every heat-map string quotes, because each of them is triggered by this same
// threshold. A string gated on one count and quoting another can fire on a heavy range and describe a
// light one, and a pin-derived count additionally reads 0 for the whole of the /pins fetch.
export const getVisibleCheckInCount = createSelector(getSelectedUsersEvents, (events): number => {
  const checkInActivityIds = new Set<Activity["id"]>();
  events.forEach((event) => {
    const checkInRef = getCheckInEntityRef(event);
    if (checkInRef) {
      checkInActivityIds.add(checkInRef.activity.id);
    }
  });
  return checkInActivityIds.size;
});

// The single home of the threshold rule. The default mode reads it, and the view toggle asks it
// whether leaving the heat map needs a confirmation, so the two can never drift apart. The
// comparison is inclusive, so the threshold value itself already counts as heavy — that matches the
// "100+ check-ins" wording the notification shows.
export const getIsAboveHeatmapThreshold = createSelector(
  getVisibleCheckInCount,
  (checkInCount): boolean => checkInCount >= HEATMAP_CHECKIN_THRESHOLD
);

// Everything the map can draw, check-in or not. The toggle stays available whenever there is data
// to render, because the heatmap draws every geo event while the threshold counts check-ins only.
export const getVisibleUserLocationCount = createSelector(
  getVisibleUserLocations,
  (locations): number => locations.length
);

// The check-in count rule on its own. Above the threshold the map shows per-rep heatmap clouds
// instead of pins. Undefined while the EVENTS load means "not decided yet", which consumers handle.
// Gated on the events fetch rather than the pins fetch: the count no longer reads the pins, so
// waiting for them only delayed the heat map behind a request it does not use.
export const getAutoHeatmapMode = createSelector(
  getIsAboveHeatmapThreshold,
  isUserLocationLoading,
  (aboveThreshold, loading): boolean | undefined => (loading ? undefined : aboveThreshold)
);

// The rep's explicit choice for this panel session, or undefined when they have not chosen.
export const getManualHeatmapMode = createSelector(
  userLocationsViewState,
  ({ manualHeatmapMode }) => manualHeatmapMode
);

// The mode the map renders. A manual choice wins over the count rule until the panel closes, at
// which point the reducer clears it and the count rule decides again.
export const getIsHeatmapMode = createSelector(
  getManualHeatmapMode,
  getAutoHeatmapMode,
  (manualMode, autoMode): boolean | undefined => manualMode ?? autoMode
);

// Whether the current user may view the Field View (User Location) layer at all — composes the
// isUserLocationLayerAvailable predicate (Field View add-on + the org's USER_LOCATION_VISIBILITY
// preference + the viewer's role) over store state. The single gate shared by every Field View entry
// point (legend, map layer, side panel, dashboard header).
export const getIsUserLocationLayerAvailable = createSelector(
  getMe,
  getOrganizationSettings,
  isFieldViewEnabled,
  (currentUser, settings, hasFieldViewAddon): boolean =>
    !!currentUser && isUserLocationLayerAvailable(currentUser, settings, hasFieldViewAddon)
);

// True when the Field View (User Location) side panel is on screen: the layer is available and at
// least one rep is selected. Toggling the layer off clears the selection, so a non-empty selection
// also implies the layer is visible.
export const isUserLocationSidebarVisible = createSelector(
  getIsUserLocationLayerAvailable,
  getSelectedUsers,
  (isLayerAvailable, selectedUsers): boolean => isLayerAvailable && selectedUsers.length > 0
);
